import { grey } from '@mui/material/colors';
import { type Color, createTheme, type Theme, type ThemeOptions } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface TypeText {
    error: string;
  }

  interface Palette {
    bidlogiq: BidlogiqPalette;
    grey: Color;
    greyLight: string;
    greyContrast: string;
    greyRange: GreyPalette;
    Tooltip: {
      bg: string;
      color: string;
    };
  }

  interface PaletteOptions {
    bidlogiq?: BidlogiqPalette;
    grey?: Partial<Color>;
    greyLight?: string;
    greyContrast?: string;
    greyRange?: GreyPalette;
    Tooltip?: {
      bg: string;
      color: string;
    };
  }

  interface Theme {
    drawerWidth: number;
  }

  interface ThemeOptions {
    drawerWidth?: number;
  }

  interface PaletteColor {
    accent2?: string;
  }

  interface SimplePaletteColorOptions {
    accent2?: string;
  }
}

interface BidlogiqPalette {
  orange: string;
  primaryOrange?: string;
  brick: string;
  rust: string;
  brown: string;
  charcoal: string;
  camel: string;
  taupe: string;
  white: string;
  black: string;
  rust50: string;
}

interface GreyPalette {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  A100: string;
  A200: string;
  A400: string;
  A700: string;
  default: string;
}

interface CommonPalette {
  white: string;
  black: string;
}

const bidlogiqPalette: BidlogiqPalette = {
  orange: '#FF6800',
  primaryOrange: '#FF6800',
  brick: '#D05C09',
  rust: '#812A16',
  brown: '#341711',
  charcoal: '#231e21',
  camel: '#d39951',
  taupe: '#d6c4ba',
  white: '#ffffff',
  black: '#000000',
  rust50: 'rgba(129, 42, 22, 0.2)',
};

const muiGreyPalette: GreyPalette = {
  50: '#fafafa',
  100: '#f5f5f5',
  200: '#eeeeee',
  300: '#e0e0e0',
  400: '#bdbdbd',
  500: '#9e9e9e',
  600: '#757575',
  700: '#616161',
  800: '#424242',
  900: '#212121',
  A100: '#f5f5f5',
  A200: '#eeeeee',
  A400: '#bdbdbd',
  A700: '#616161',
  default: '#fff',
};

const muiCommonPalette: CommonPalette = {
  white: '#fff',
  black: '#000',
};

const drawerWidth = 250;

const commonThemeOptions: ThemeOptions = {
  drawerWidth,
  typography: {
    htmlFontSize: 18,
    fontFamily: ['Montserrat', 'sans-serif'].join(','),
    h5: {
      fontWeight: 600,
    },
    button: {
      textTransform: 'none',
    },
  },
};

const lightPalette = {
  mode: 'light' as const,
  primary: {
    main: bidlogiqPalette.charcoal,
    light: bidlogiqPalette.taupe,
    accent2: bidlogiqPalette.brick,
    contrastText: bidlogiqPalette.white,
  },
  secondary: {
    main: bidlogiqPalette.orange,
    light: bidlogiqPalette.camel,
    dark: bidlogiqPalette.brick,
    contrastText: bidlogiqPalette.black,
  },
  error: {
    main: bidlogiqPalette.rust,
  },
  warning: {
    main: '#8c5a23', // Darkened camel for WCAG AA compliance on light backgrounds
    contrastText: '#ffffff',
  },
  info: {
    main: bidlogiqPalette.charcoal,
  },
  success: {
    main: '#2e7d32',
  },
  background: {
    default: muiGreyPalette[200],
    paper: muiGreyPalette[200],
  },
  text: {
    primary: bidlogiqPalette.black,
    secondary: bidlogiqPalette.black,
    error: bidlogiqPalette.rust,
  },
  grey: grey,
  greyLight: muiGreyPalette[300],
  greyContrast: muiGreyPalette[800],
  greyRange: muiGreyPalette,
  common: muiCommonPalette,
  bidlogiq: bidlogiqPalette,
  Tooltip: {
    bg: bidlogiqPalette.orange,
    color: bidlogiqPalette.black,
  },
};

const darkPalette = {
  mode: 'dark' as const,
  primary: {
    main: bidlogiqPalette.orange,
    light: bidlogiqPalette.taupe,
    accent2: bidlogiqPalette.orange,
    contrastText: bidlogiqPalette.charcoal,
  },
  secondary: {
    main: bidlogiqPalette.taupe,
    light: bidlogiqPalette.white,
    dark: bidlogiqPalette.camel,
    contrastText: bidlogiqPalette.black,
  },
  error: {
    main: bidlogiqPalette.rust,
  },
  warning: {
    main: bidlogiqPalette.camel,
    contrastText: bidlogiqPalette.black,
  },
  info: {
    main: bidlogiqPalette.taupe,
    contrastText: bidlogiqPalette.black,
  },
  success: {
    main: '#66bb6a',
    contrastText: '#000000',
  },
  background: {
    default: bidlogiqPalette.charcoal,
    paper: bidlogiqPalette.charcoal,
  },
  text: {
    primary: bidlogiqPalette.white,
    secondary: bidlogiqPalette.white,
    error: bidlogiqPalette.camel,
  },
  grey: grey,
  greyLight: muiGreyPalette[800],
  greyContrast: muiGreyPalette[200],
  greyRange: muiGreyPalette,
  common: muiCommonPalette,
  bidlogiq: bidlogiqPalette,
  Tooltip: {
    bg: bidlogiqPalette.orange,
    color: bidlogiqPalette.black,
  },
};

export const lightTheme: Theme = createTheme({
  ...commonThemeOptions,
  palette: lightPalette,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: lightPalette.background.default,
        },
        a: {
          // Make all mailto links use BIDLOGIQ brick
          '&[href^="mailto:"]:not(.MuiButton-root)': {
            color: bidlogiqPalette.brick,
            textDecorationColor: bidlogiqPalette.brick,
          },
          '&[href^="mailto:"]:not(.MuiButton-root):hover': {
            color: bidlogiqPalette.rust,
            textDecorationColor: bidlogiqPalette.rust,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          borderRadius: '8px',
          ...(ownerState.variant === 'contained' &&
            (!ownerState.color || ownerState.color === 'primary') && {
              backgroundColor: bidlogiqPalette.brick,
              color: bidlogiqPalette.white,
              '&:hover': {
                backgroundColor: bidlogiqPalette.rust,
              },
            }),
        }),
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardError: {
          backgroundColor: 'rgba(129, 42, 22, 0.1)', // Rust 10%
          color: bidlogiqPalette.rust,
          '& .MuiAlert-icon': {
            color: bidlogiqPalette.rust,
          },
        },
        standardWarning: {
          backgroundColor: 'rgba(211, 153, 81, 0.15)', // Camel 15%
          color: bidlogiqPalette.brown,
          '& .MuiAlert-icon': {
            color: bidlogiqPalette.brick,
          },
        },
        standardInfo: {
          backgroundColor: 'rgba(35, 30, 33, 0.05)', // Charcoal 5%
          color: bidlogiqPalette.charcoal,
          '& .MuiAlert-icon': {
            color: bidlogiqPalette.charcoal,
          },
        },
        standardSuccess: {
          backgroundColor: 'rgba(46, 125, 50, 0.1)',
          color: '#1b5e20',
          '& .MuiAlert-icon': {
            color: '#2e7d32',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: bidlogiqPalette.white,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.3s, color 0.3s',
          '&:hover': {
            backgroundColor: lightPalette.greyLight,
            '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
              color: lightPalette.text.primary,
            },
          },
          '&.Mui-selected': {
            backgroundColor: lightPalette.greyContrast,
            color: `${lightPalette.primary.contrastText} !important`,
            '&:hover': {
              backgroundColor: lightPalette.greyContrast,
            },
            '& .MuiListItemIcon-root': {
              color: `${lightPalette.primary.contrastText} !important`,
            },
            '& .MuiListItemIcon-root svg path': {
              fill: `${lightPalette.primary.contrastText} !important`,
              stroke: `${lightPalette.primary.contrastText} !important`,
            },
            '& .MuiListItemText-primary': {
              color: `${lightPalette.primary.contrastText} !important`,
              fontWeight: 600,
            },
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: `${lightPalette.greyContrast} !important`,
            color: `${lightPalette.primary.contrastText} !important`,
            '& .MuiTypography-root': {
              color: `${lightPalette.primary.contrastText} !important`,
            },
            '&:hover': {
              backgroundColor: `${lightPalette.greyContrast} !important`,
            },
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: `${lightPalette.greyContrast} !important`,
            '& .MuiTableCell-root': {
              color: `${lightPalette.primary.contrastText} !important`,
            },
            '&:hover': {
              backgroundColor: `${lightPalette.greyContrast} !important`,
            },
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '1.15rem',
          fontWeight: 500,
          color: lightPalette.text.primary,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& input:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 1000px #ffffff inset',
            WebkitTextFillColor: '#000',
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: bidlogiqPalette.orange,
          color: muiCommonPalette.white,
          fontWeight: 'bold',
        },
      },
    },
  },
});

export const darkTheme: Theme = createTheme({
  ...commonThemeOptions,
  palette: darkPalette,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: darkPalette.background.default,
        },
        a: {
          '&[href^="mailto:"]:not(.MuiButton-root)': {
            color: bidlogiqPalette.taupe,
            textDecorationColor: bidlogiqPalette.taupe,
          },
          '&[href^="mailto:"]:not(.MuiButton-root):hover': {
            color: bidlogiqPalette.white,
            textDecorationColor: bidlogiqPalette.white,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          borderRadius: '8px',
          ...(ownerState.variant === 'contained' &&
            (!ownerState.color || ownerState.color === 'primary') && {
              backgroundColor: bidlogiqPalette.brick,
              color: bidlogiqPalette.white,
              '&:hover': {
                backgroundColor: bidlogiqPalette.rust,
              },
            }),
        }),
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardError: {
          backgroundColor: 'rgba(208, 92, 9, 0.25)', // Brick 25%
          color: '#ffccbc',
          '& .MuiAlert-icon': {
            color: bidlogiqPalette.brick,
          },
        },
        standardWarning: {
          backgroundColor: 'rgba(211, 153, 81, 0.2)', // Camel 20%
          color: '#ffe0b2',
          '& .MuiAlert-icon': {
            color: bidlogiqPalette.camel,
          },
        },
        standardInfo: {
          backgroundColor: 'rgba(214, 196, 186, 0.15)', // Taupe 15%
          color: bidlogiqPalette.white,
          '& .MuiAlert-icon': {
            color: bidlogiqPalette.taupe,
          },
        },
        standardSuccess: {
          backgroundColor: 'rgba(102, 187, 106, 0.15)',
          color: '#a5d6a7',
          '& .MuiAlert-icon': {
            color: '#66bb6a',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: darkPalette.background.paper,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          color: darkPalette.text.primary,
          transition: 'background-color 0.3s, color 0.3s',
          '&:hover': {
            backgroundColor: darkPalette.greyLight,
            '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
              color: darkPalette.text.primary,
            },
          },
          '&.Mui-selected': {
            backgroundColor: bidlogiqPalette.brick,
            color: `${bidlogiqPalette.white} !important`,
            '&:hover': {
              backgroundColor: bidlogiqPalette.rust,
            },
            '& .MuiListItemIcon-root': {
              color: `${bidlogiqPalette.white} !important`,
            },
            '& .MuiListItemIcon-root svg path': {
              fill: `${bidlogiqPalette.white} !important`,
              stroke: `${bidlogiqPalette.white} !important`,
            },
            '& .MuiListItemText-primary': {
              color: `${bidlogiqPalette.white} !important`,
              fontWeight: 600,
            },
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: `${darkPalette.greyContrast} !important`,
            color: `${darkPalette.primary.contrastText} !important`,
            '&:hover': {
              backgroundColor: `${darkPalette.greyContrast} !important`,
            },
            '& .MuiTypography-root': {
              color: 'inherit !important',
            },
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: `${darkPalette.greyContrast} !important`,
            '& .MuiTableCell-root': {
              color: `${darkPalette.primary.contrastText} !important`,
            },
            '&:hover': {
              backgroundColor: `${darkPalette.greyContrast} !important`,
            },
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '1.15rem',
          fontWeight: 500,
          color: darkPalette.text.primary,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& input:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 1000px #3d363c inset',
            WebkitTextFillColor: darkPalette.text.primary,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: `${darkPalette.text.primary} !important`,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          color: darkPalette.text.primary,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: bidlogiqPalette.orange,
          color: muiCommonPalette.white,
          fontWeight: 'bold',
        },
      },
    },
  },
});

export type { BidlogiqPalette, CommonPalette, GreyPalette };
